import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Box,
  Text,
  Stepper,
  Step,
  StepIndicator,
  StepStatus,
  StepIcon,
  StepNumber,
  StepSeparator,
  StepTitle,
  StepDescription,
  FormControl,
  FormLabel,
  Input,
  Flex,
  SimpleGrid,
  Select,
  Stack,
  Divider,
  Textarea,
  useToast,
  GridItem,
  InputGroup,
  InputRightElement,
  IconButton,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { getData } from '../../utils/httpClient';
import DocumentInput from '../common/DocumentInput';

const steps = [
  { title: 'Datos Personales', description: 'Información básica del docente' },
  { title: 'Contacto y Dirección', description: 'Datos de contacto y ubicación' },
  { title: 'Confirmar', description: 'Verifica los datos antes de agregar' },
];

export default function CreateTeacherModal({
  isOpen,
  onClose,
  formData,
  setFormData,
  onConfirm,
}) {
  const [activeStep, setActiveStep] = useState(0);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [sexTypes, setSexTypes] = useState([]);
  const [employeeTypes, setEmployeeTypes] = useState([]);
  const [isSearchingDocument, setIsSearchingDocument] = useState(false);

  const toast = useToast();

  const getDocumentTypes = async () => {
    try {
      const response = await getData('/documentTypes');
      setDocumentTypes(response?.payload?.documentTypes || []);
    } catch (error) {
      toast({
        title: 'Error al cargar los tipos de documento.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getSexTypes = async () => {
    try {
      const response = await getData('/sexTypes');
      setSexTypes(response?.payload?.sexTypes || []);
    } catch (error) {
      toast({
        title: 'Error al cargar los tipos de sexo.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getEmployeeTypes = async () => {
    try {
      const response = await getData('/employeeTypes');
      setEmployeeTypes(response?.payload?.employeeTypes || []);
    } catch (error) {
      toast({
        title: 'Error al cargar los tipos de empleado.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const checkDocumentExists = async () => {
    if (!formData.document) {
      toast({
        title: 'Ingrese un documento para verificar',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSearchingDocument(true);
    try {
      const response = await getData(`/person/search/document/${formData.document}`);
      if (response?.payload?.exists) {
        toast({
          title: 'Documento ya existe',
          description: 'Ya existe una persona registrada con este documento',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Documento disponible',
          description: 'El documento está disponible para usar',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error al verificar documento',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSearchingDocument(false);
    }
  };

  useEffect(() => {
    getDocumentTypes();
    getSexTypes();
    getEmployeeTypes();
  }, []);

  const handleNextStep = () => {
    if (validateStep()) {
      setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
    } else {
      toast({
        title: 'Por favor completa todos los campos requeridos.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handlePrevStep = () => setActiveStep((prev) => Math.max(prev - 1, 0));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateStep = () => {
    switch (activeStep) {
      case 0:
        return (
          formData.name &&
          formData.lastName &&
          formData.documentTypeId &&
          formData.document &&
          formData.birthDate &&
          formData.sexId &&
          formData.birthPlace
        );
      case 1:
        return (
          formData.cellPhone &&
          formData.email &&
          formData.street &&
          formData.number &&
          formData.city
        );
      default:
        return true;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent
        maxWidth={{ base: '100%', md: '80%' }}
        minHeight={'85vh'}
        maxHeight={'85vh'}
        overflowY="auto"
      >
        <ModalHeader mb={5}>Agregar Docente</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex
            direction={{ base: 'column', md: 'row' }}
            justify="space-between"
            align="center"
          >
            <Box
              flex="0 0 30%"
              mr={6}
              display={{ base: 'none', xl: 'block' }}
              height="60vh"
            >
              <Stepper
                index={activeStep}
                orientation="vertical"
                gap="4"
                height="100%"
                display="flex"
                flexDirection="column"
              >
                {steps.map((step, index) => (
                  <Step key={index} flex="1">
                    <StepIndicator>
                      <StepStatus
                        complete={<StepIcon />}
                        incomplete={<StepNumber />}
                        active={<StepNumber />}
                      />
                    </StepIndicator>
                    <Box flexShrink="0">
                      <StepTitle>{step.title}</StepTitle>
                      <StepDescription>{step.description}</StepDescription>
                    </Box>
                    <StepSeparator />
                  </Step>
                ))}
              </Stepper>
            </Box>
            <Box flex="1" mb={5}>
              {activeStep === 0 && (
                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5} mt={4}>
                  <Text
                    fontSize="lg"
                    fontWeight="bold"
                    mb={2}
                    gridColumn="span 2"
                  >
                    Datos personales
                  </Text>

                  <FormControl id="name" isRequired>
                    <FormLabel>Nombre</FormLabel>
                    <Input
                      placeholder="Nombre"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  <FormControl id="secondName">
                    <FormLabel>Segundo Nombre</FormLabel>
                    <Input
                      placeholder="Segundo Nombre"
                      name="secondName"
                      value={formData.secondName}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  <FormControl id="lastName" isRequired>
                    <FormLabel>Apellido</FormLabel>
                    <Input
                      placeholder="Apellido"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  <FormControl id="maternalLastName">
                    <FormLabel>Segundo Apellido</FormLabel>
                    <Input
                      placeholder="Segundo Apellido"
                      name="maternalLastName"
                      value={formData.maternalLastName}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  <FormControl id="documentTypeId" isRequired>
                    <FormLabel>Tipo de Documento</FormLabel>
                    <Select
                      name="documentTypeId"
                      value={formData.documentTypeId}
                      onChange={handleInputChange}
                    >
                      <option value="">Seleccione</option>
                      {documentTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.description}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl id="document" isRequired>
                    <FormLabel>Documento</FormLabel>
                    <InputGroup>
                      <DocumentInput
                        placeholder="Documento"
                        name="document"
                        value={formData.document}
                        onChange={handleInputChange}
                      />
                      <InputRightElement>
                        <IconButton
                          icon={<SearchIcon />}
                          size="sm"
                          onClick={checkDocumentExists}
                          aria-label="Verificar documento"
                        />
                      </InputRightElement>
                    </InputGroup>
                  </FormControl>

                  <FormControl id="sexId" isRequired>
                    <FormLabel>Sexo</FormLabel>
                    <Select
                      name="sexId"
                      value={formData.sexId}
                      onChange={handleInputChange}
                    >
                      <option value="">Seleccione</option>
                      {sexTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.description}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl id="birthDate" isRequired>
                    <FormLabel>Fecha de Nacimiento</FormLabel>
                    <Input
                      type="date"
                      name="birthDate"
                      value={formData.birthDate}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  <FormControl id="birthPlace" isRequired>
                    <FormLabel>Lugar de Nacimiento</FormLabel>
                    <Input
                      placeholder="Lugar de Nacimiento"
                      name="birthPlace"
                      value={formData.birthPlace}
                      onChange={handleInputChange}
                    />
                  </FormControl>
                </SimpleGrid>
              )}

              {activeStep === 1 && (
                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5} mt={4}>
                  <Text
                    fontSize="lg"
                    fontWeight="bold"
                    mb={2}
                    gridColumn="span 2"
                  >
                    Contacto
                  </Text>

                  <FormControl id="email" isRequired>
                    <FormLabel>Correo Electrónico</FormLabel>
                    <Input
                      type="email"
                      placeholder="Correo Electrónico"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  <FormControl id="phone">
                    <FormLabel>Teléfono</FormLabel>
                    <Input
                      placeholder="Teléfono"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  <FormControl id="cellPhone" isRequired>
                    <FormLabel>Celular</FormLabel>
                    <Input
                      placeholder="Celular"
                      name="cellPhone"
                      value={formData.cellPhone}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  <Divider my={6} gridColumn="span 2" />

                  <Text
                    fontSize="lg"
                    fontWeight="bold"
                    mb={2}
                    gridColumn="span 2"
                  >
                    Dirección
                  </Text>

                  <FormControl id="street" isRequired>
                    <FormLabel>Calle</FormLabel>
                    <Input
                      placeholder="Calle"
                      name="street"
                      value={formData.street}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  <FormControl id="number" isRequired>
                    <FormLabel>Número</FormLabel>
                    <Input
                      placeholder="Número"
                      name="number"
                      value={formData.number}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  <FormControl id="apartment">
                    <FormLabel>Apartamento</FormLabel>
                    <Input
                      placeholder="Apartamento"
                      name="apartment"
                      value={formData.apartment}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  <FormControl id="city" isRequired>
                    <FormLabel>Ciudad</FormLabel>
                    <Input
                      placeholder="Ciudad"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  <FormControl id="locality">
                    <FormLabel>Localidad</FormLabel>
                    <Input
                      placeholder="Localidad"
                      name="locality"
                      value={formData.locality}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  <FormControl id="zipCode">
                    <FormLabel>Código Postal</FormLabel>
                    <Input
                      placeholder="Código Postal"
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleInputChange}
                    />
                  </FormControl>
                </SimpleGrid>
              )}

              {activeStep === 2 && (
                <SimpleGrid columns={{ base: 1, lg: 1 }} spacing={5} mt={4}>
                  <Text
                    fontSize="xl"
                    fontWeight="bold"
                    mb={6}
                    color="gray.700"
                  >
                    Confirmación de Datos del Docente
                  </Text>

                  <Box>
                    <Text fontSize="lg" fontWeight="semibold" color="gray.600" mb={3}>
                      Datos Personales
                    </Text>
                    <Text><strong>Nombre Completo:</strong> {formData.name} {formData.secondName} {formData.lastName} {formData.maternalLastName}</Text>
                    <Text><strong>Documento:</strong> {formData.document}</Text>
                    <Text><strong>Fecha de Nacimiento:</strong> {formData.birthDate}</Text>
                    <Text><strong>Lugar de Nacimiento:</strong> {formData.birthPlace}</Text>
                    <Text><strong>Sexo:</strong> {sexTypes.find(s => s.id == formData.sexId)?.description}</Text>
                  </Box>

                  <Divider my={4} />

                  <Box>
                    <Text fontSize="lg" fontWeight="semibold" color="gray.600" mb={3}>
                      Contacto
                    </Text>
                    <Text><strong>Email:</strong> {formData.email}</Text>
                    <Text><strong>Teléfono:</strong> {formData.phone}</Text>
                    <Text><strong>Celular:</strong> {formData.cellPhone}</Text>
                  </Box>

                  <Divider my={4} />

                  <Box>
                    <Text fontSize="lg" fontWeight="semibold" color="gray.600" mb={3}>
                      Dirección
                    </Text>
                    <Text><strong>Dirección:</strong> {formData.street} {formData.number} {formData.apartment && `, Apt ${formData.apartment}`}</Text>
                    <Text><strong>Ciudad:</strong> {formData.city}</Text>
                    <Text><strong>Localidad:</strong> {formData.locality}</Text>
                    <Text><strong>Código Postal:</strong> {formData.zipCode}</Text>
                  </Box>
                </SimpleGrid>
              )}
            </Box>
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Box>
            <Button
              colorScheme="blue"
              mr={3}
              onClick={handlePrevStep}
              isDisabled={activeStep === 0}
              width="120px"
            >
              Atrás
            </Button>
            {activeStep === steps.length - 1 ? (
              <Button
                colorScheme="blue"
                onClick={onConfirm}
                width="120px"
              >
                Completar
              </Button>
            ) : (
              <Button
                colorScheme="blue"
                onClick={handleNextStep}
                isDisabled={activeStep === steps.length - 1}
                width="120px"
              >
                Siguiente
              </Button>
            )}
          </Box>
        </ModalFooter>
        <Box display={{ base: 'block', xl: 'none' }} mt={4}>
          <Stack spacing={4}>
            <Stepper size="sm" index={activeStep} gap="0">
              {steps.map((step, index) => (
                <Step key={index}>
                  <StepIndicator>
                    <StepStatus complete={<StepIcon />} />
                  </StepIndicator>
                  <StepSeparator _horizontal={{ ml: '0' }} />
                </Step>
              ))}
            </Stepper>
          </Stack>
        </Box>
      </ModalContent>
    </Modal>
  );
}
